import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader } from '@/components/PageHeader';
import { MandatoryFieldValidator } from '@/components/MandatoryFieldValidator';
import { ReviewTaskManager } from '@/components/ReviewTaskManager';
import { ProductionReviewDashboard } from '@/components/ProductionReviewDashboard';
import { SystemHealthDashboard } from '@/components/SystemHealthDashboard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useReviewTasks } from '@/hooks/useReviewTasks';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, AlertTriangle, Users } from 'lucide-react';

const ReviewDashboard: React.FC = () => {
  const { 
    pendingTasks, 
    inProgressTasks, 
    completedTasks, 
    highPriorityTasks,
    tasks,
    isLoading 
  } = useReviewTasks();

  return (
    <div className="space-y-6">
      <PageHeader
        title="Manual Review Dashboard"
        description="Phase 4: Mandatory field validation and manual review workflow"
      />

      <Tabs defaultValue="enhanced" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="enhanced">Enhanced Dashboard</TabsTrigger>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="fields">Field Configuration</TabsTrigger>
          <TabsTrigger value="tasks">Review Tasks</TabsTrigger>
          <TabsTrigger value="health">System Health</TabsTrigger>
        </TabsList>

        <TabsContent value="enhanced" className="space-y-6">
          <ProductionReviewDashboard />
        </TabsContent>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending Reviews</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{pendingTasks.length}</div>
                <p className="text-xs text-muted-foreground">Awaiting review</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">In Progress</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{inProgressTasks.length}</div>
                <p className="text-xs text-muted-foreground">Being reviewed</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Completed</CardTitle>
                <CheckCircle className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{completedTasks.length}</div>
                <p className="text-xs text-muted-foreground">Reviews finished</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">High Priority</CardTitle>
                <AlertTriangle className="h-4 w-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">{highPriorityTasks.length}</div>
                <p className="text-xs text-muted-foreground">Urgent reviews</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Review Queue Status</CardTitle>
              <CardDescription>Current status of manual review tasks</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {isLoading ? (
                  <div className="text-center py-8">Loading review tasks...</div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Review task interface will be enhanced in the next phase.
                    <br />
                    Database foundation and field validation are now ready.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fields" className="space-y-6">
          <MandatoryFieldValidator />
        </TabsContent>

        <TabsContent value="tasks" className="space-y-6">
          <ReviewTaskManager 
            tasks={tasks || []} 
            onTaskUpdate={() => {
              // Force a refresh of the data
              window.location.reload();
            }}
          />
        </TabsContent>

        <TabsContent value="health" className="space-y-6">
          <SystemHealthDashboard />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReviewDashboard;